import React, { useState } from "react";
import "./UserAccount.css";
import {
  FaCoins,
  FaPencilAlt,
  FaTimes,
  FaCheck,
  FaChevronRight,
  FaLock,
  FaCalendarAlt,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { refreshUserUser, selectUser } from "../../../Store/userSlice";
import UserService from "../../../api/UserService";
import { useNavigate } from "react-router-dom";

const UserAccount = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [firstname, setFirstname] = useState(user.firstname);
  const [lastname, setLastname] = useState(user.lastname);
  const [phone, setPhone] = useState(user.phone);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState("");

  const initials = `${user.firstname?.[0] || ""}${user.lastname?.[0] || ""}`.toUpperCase();

  const cancelEdit = () => {
    setFirstname(user.firstname);
    setLastname(user.lastname);
    setPhone(user.phone);
    setError("");
    setEditMode(false);
  };

  const saveProfile = async () => {
    const phonePattern = /^\d{8}$/;
    const namePattern = /^[A-Za-zÀ-ÿ\s]+$/;

    if (!firstname.trim() || !namePattern.test(firstname)) {
      setError("Prénom invalide — lettres uniquement.");
      return;
    }
    if (!lastname.trim() || !namePattern.test(lastname)) {
      setError("Nom invalide — lettres uniquement.");
      return;
    }
    if (!phonePattern.test(phone)) {
      setError("Le numéro de téléphone doit comporter 8 chiffres.");
      return;
    }

    setError("");
    setSaving(true);
    const token = JSON.parse(localStorage.getItem("token")).accessToken;
    try {
      await UserService.editProfile(token, {
        firstname,
        lastname,
        phone,
      });
      await dispatch(refreshUserUser());
      setEditMode(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-page">
        <div className="profile-hero">
          <div className="profile-hero-initials">{initials}</div>
          <div>
            <h2 className="profile-hero-name">
              {user.firstname} {user.lastname}
            </h2>
            <p className="profile-hero-email">{user.email}</p>
          </div>
        </div>

        <div className="profile-content">
          {/* Jetons */}
          <div className="profile-card profile-card--jetons">
            <FaCoins className="jetons-icon" style={{ color: "#f59e0b" }} />
            <div>
              <span className="jetons-count">{user.jetons}</span>
              <span className="jetons-label">jetons disponibles</span>
            </div>
          </div>

          {/* Personal info */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h3 className="profile-card-title">Informations personnelles</h3>
              {!editMode && (
                <button
                  className="profile-edit-btn"
                  onClick={() => setEditMode(true)}
                >
                  <FaPencilAlt />
                  Modifier
                </button>
              )}
            </div>

            {editMode ? (
              <div className="profile-form">
                <div className="profile-form-row">
                  <div className="profile-field">
                    <label>Prénom</label>
                    <input
                      value={firstname}
                      onChange={(e) => { setFirstname(e.target.value); setError(""); }}
                      placeholder="Prénom"
                    />
                  </div>
                  <div className="profile-field">
                    <label>Nom</label>
                    <input
                      value={lastname}
                      onChange={(e) => { setLastname(e.target.value); setError(""); }}
                      placeholder="Nom"
                    />
                  </div>
                </div>
                <div className="profile-form-row">
                  <div className="profile-field">
                    <label>Téléphone</label>
                    <input
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); setError(""); }}
                      placeholder="8 chiffres"
                      maxLength={8}
                    />
                  </div>
                </div>
                {error && <div className="profile-error-banner">{error}</div>}
                <div className="profile-form-actions">
                  <button
                    className="profile-btn profile-btn--cancel"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    <FaTimes />
                    Annuler
                  </button>
                  <button
                    className="profile-btn profile-btn--save"
                    onClick={saveProfile}
                    disabled={saving}
                  >
                    <FaCheck />
                    {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>
              </div>
            ) : (
              <>
              {savedOk && (
                <div className="profile-saved-banner">
                  <FaCheck className="profile-saved-icon" />
                  Modifications enregistrées
                </div>
              )}
              <div className="profile-info-list">
                <div className="profile-info-row">
                  <span className="profile-info-label">Prénom</span>
                  <span className="profile-info-value">{user.firstname}</span>
                </div>
                <div className="profile-info-row">
                  <span className="profile-info-label">Nom</span>
                  <span className="profile-info-value">{user.lastname}</span>
                </div>
                <div className="profile-info-row">
                  <span className="profile-info-label">Email</span>
                  <span className="profile-info-value">{user.email}</span>
                </div>
                <div className="profile-info-row">
                  <span className="profile-info-label">Téléphone</span>
                  <span className="profile-info-value">{user.phone}</span>
                </div>
              </div>
              </>
            )}
          </div>

          {/* Nav rows */}
          <button
            className="profile-nav-row"
            onClick={() => navigate("/my-reservation")}
          >
            <div className="profile-nav-row-left">
              <div className="profile-nav-row-icon">
                <FaCalendarAlt />
              </div>
              <div>
                <span className="profile-nav-row-title">Mes réservations</span>
                <span className="profile-nav-row-sub">
                  Consulter votre planning et historique
                </span>
              </div>
            </div>
            <FaChevronRight className="profile-nav-row-chevron" />
          </button>

          <button
            className="profile-nav-row"
            onClick={() => navigate("/change-password")}
          >
            <div className="profile-nav-row-left">
              <div className="profile-nav-row-icon">
                <FaLock />
              </div>
              <div>
                <span className="profile-nav-row-title">Mot de passe</span>
                <span className="profile-nav-row-sub">
                  Modifier votre mot de passe
                </span>
              </div>
            </div>
            <FaChevronRight className="profile-nav-row-chevron" />
          </button>
        </div>
    </div>
  );
};

export default UserAccount;
